import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { ServerConfig } from 'config'

import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"

import { makeStyles } from '@material-ui/core/styles'
import {
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
} from '@material-ui/core'

import DeleteConfirmDialog from '../../components/EntryListAndEdit/DeleteConfirmDialog'

const useStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(3),
  },
  infoPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  protectedPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
  },
  protectedPaperEmpty: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: '#e8f5e9',
    border: '1px solid #c8e6c9',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  resultPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: '#e8f5e9',
    border: '1px solid #c8e6c9',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: theme.spacing(1),
  },
}))

const ResetTenantTrainingTransactions = () => {
  const classes = useStyles()
  const auth = useSelector((state) => state.auth)

  // computed lazily (not at module top-level) - ServerConfig comes through a
  // circular import chain (config -> PrivateRoutesConfig -> AdminPage -> here),
  // so reading it eagerly during module evaluation can see it before it's
  // initialized; every other admin tab only reads it inside functions/JSX
  const RESET_URL = `${ServerConfig.SERVER_URL}/admin/training_cleanup/reset_tenant_transactions`

  const [tenants, setTenants] = useState([])
  const [tenantId, setTenantId] = useState('')

  const oneHourAgo = new Date()
  oneHourAgo.setHours(oneHourAgo.getHours() - 1)
  const [startDate, setStartDate] = useState(oneHourAgo)
  const [endDate, setEndDate] = useState(new Date())

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [report, setReport] = useState(null)
  const [previewKey, setPreviewKey] = useState(null)
  const [showConfirm, setShowConfirm] = useState(false)

  const authConfig = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth.access_token}`,
    },
  }

  useEffect(() => {
    axios
      .get(`${ServerConfig.SERVER_URL}/admin/tenants`, authConfig)
      .then((res) => setTenants(res.data))
  }, [])

  const currentKey = () =>
    JSON.stringify({
      tenantId,
      start: startDate.getTime(),
      end: endDate.getTime(),
    })

  const requestBody = (dryRun) => ({
    tenant_id: tenantId,
    start_ts_utc: startDate.getTime(),
    end_ts_utc: endDate.getTime(),
    dry_run: dryRun,
    confirm: !dryRun,
  })

  const fetchPreview = async () => {
    if (!tenantId) {
      setError('Please select a tenant')
      return
    }
    if (startDate.getTime() >= endDate.getTime()) {
      setError('Start date must be before end date')
      return
    }

    setError(null)
    setIsLoading(true)
    setShowConfirm(false)
    try {
      const res = await axios.post(RESET_URL, requestBody(true), authConfig)
      setReport(res.data)
      setPreviewKey(currentKey())
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch preview')
      setReport(null)
      setPreviewKey(null)
    } finally {
      setIsLoading(false)
    }
  }

  const executeReset = async () => {
    setError(null)
    setIsLoading(true)
    try {
      const res = await axios.post(RESET_URL, requestBody(false), authConfig)
      setReport(res.data)
      setPreviewKey(null)
      setShowConfirm(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to execute reset')
    } finally {
      setIsLoading(false)
    }
  }

  // the Execute step may only fire against a preview fetched for these exact
  // parameters, in this session - any change to a field invalidates it
  const previewIsCurrent =
    report && report.dry_run === true && previewKey === currentKey()

  const protectedCount = report?.counts?.ledger_protected ?? 0

  return (
    <>
      <Typography variant="body2" style={{ marginBottom: 16 }}>
        Deletes the task/recyclable/ledger footprint of a training session for one
        tenant within a time window, so it can resume with a clean ledger. Any
        transaction proving real money left via PayPal/Venmo/Checkbook/ONBE - and
        its paired balance entry - is always left untouched and reported below;
        there is no way to override that from this page.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption">Tenant</Typography>
          <br />
          <TextField
            select
            fullWidth
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            InputProps={{ style: { backgroundColor: '#fff' } }}
          >
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.business_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Typography variant="caption">Start (incl.)</Typography>
          <br />
          <DatePicker
            selected={startDate}
            onChange={(d) => setStartDate(d)}
            showTimeSelect
            dateFormat="MMMM d, yyyy h:mm aa"
            className="form-select"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Typography variant="caption">End (excl.)</Typography>
          <br />
          <DatePicker
            selected={endDate}
            onChange={(d) => setEndDate(d)}
            showTimeSelect
            dateFormat="MMMM d, yyyy h:mm aa"
            className="form-select"
          />
        </Grid>
      </Grid>

      <div className={classes.section}>
        <Button variant="contained" color="primary" disabled={isLoading} onClick={fetchPreview}>
          {isLoading ? <CircularProgress size={20} /> : 'Preview'}
        </Button>
      </div>

      {error && <Typography className={classes.errorText}>{error}</Typography>}

      {report && (
        <div className={classes.section}>
          <Typography variant="h6">
            {report.dry_run ? 'Preview' : 'Result'} for {report.tenant_business_name}
          </Typography>

          <table className={classes.table}>
            <tbody>
              {Object.entries(report.counts).map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td style={{ textAlign: 'right' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Paper
            className={protectedCount > 0 ? classes.protectedPaper : classes.protectedPaperEmpty}
          >
            <Typography variant="subtitle1">
              {protectedCount > 0
                ? `⚠️ ${protectedCount} row(s) proving real external payouts - will be left untouched`
                : 'No real external payouts found in this window'}
            </Typography>
            {report.protected_rows.map((r) => (
              <Typography variant="body2" key={r.ledger_id}>
                [{r.reason}] {r.transactiontype} - ${r.amount_dollars.toFixed(2)} - {r.note}
              </Typography>
            ))}
            {report.ambiguous_mirror_warnings.length > 0 && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                ⚠️ {report.ambiguous_mirror_warnings.length} ambiguous match(es) found -
                all candidates protected, review manually if needed.
              </Typography>
            )}
          </Paper>

          {(report.dr6_in_window.length > 0 || report.dr14_in_window.length > 0) && (
            <Paper className={classes.infoPaper}>
              <Typography variant="subtitle2">
                {report.dr6_in_window.length + report.dr14_in_window.length} DR6/DR14
                report(s) generated in this window - these may already be filed with
                CalRecycle/DORIIS and can't be un-filed from here.
              </Typography>
            </Paper>
          )}

          {report.invoices_in_window.length > 0 && (
            <Paper className={classes.infoPaper}>
              <Typography variant="subtitle2">
                {report.invoices_in_window.length} invoice(s) generated in this window
                - these may already have been emailed to the tenant.
              </Typography>
            </Paper>
          )}

          {previewIsCurrent && (
            <div className={classes.section}>
              <DeleteConfirmDialog
                title="Confirm permanent reset"
                message={`This will permanently delete ${report.counts.tasks} task(s) and ${report.counts.ledger_deleted} ledger row(s) for ${report.tenant_business_name}. ${protectedCount} row(s) proving real external payouts will be left untouched. This cannot be undone.`}
                triggerLabel="Execute Reset"
                confirmLabel="Yes, delete permanently"
                showTrigger={!showConfirm}
                showWarning={showConfirm}
                isLoading={isLoading}
                onTrigger={() => setShowConfirm(true)}
                onCancel={() => setShowConfirm(false)}
                onConfirm={executeReset}
              />
            </div>
          )}

          {!report.dry_run && (
            <Paper className={classes.resultPaper}>
              <Typography variant="subtitle1">
                Done. Deleted {report.counts.tasks} task(s) and{' '}
                {report.counts.ledger_deleted} ledger row(s). {protectedCount} row(s)
                left untouched.
              </Typography>
            </Paper>
          )}
        </div>
      )}
    </>
  )
}

export default ResetTenantTrainingTransactions
